import React, { useState } from "react";
import { ErrorType } from "../../../types/common-type";
import handleClearError from "../../../core/methods/handleClearError";
import ErrorMessage from "../../../core/common/ErrorMessage";
import Table from "../../../core/common/dataTable/index";
import { cellWidth } from "../../../core/methods/tableCellWidth";
import CommonFilterSelect, {
  FilterTypes,
  initialFilters,
} from "../../../core/common/CommonFilterSelect";
import { dummyData_allBulk } from "../../../core/data/js/dummy-data";
import TabItems from "../../../core/common/TabItems";
import { usePackageByDepartmentId } from "../../../core/customhook/hooks/packageHooks";
import { toast } from "react-toastify";
import useApi from "../../../core/customhook/useApi";
import webservice_url from "../../../core/webservice/Webservice_url";
import { formatDateDDMMMMYYYY } from "../../../core/methods/formattedDate";
import Loading from "../../../core/common/custom-loading/Loading";
import {
  safeDateSort,
  safeNumberSort,
  safeStringSort,
} from "../../../core/methods/sorterMethods";
import { ColumnsType } from "antd/es/table";
import ExcelJS from "exceljs";
import Swal from "sweetalert2";

export const dummyData_valid = dummyData_allBulk.filter(
  (candidate) => candidate.status === "Valid",
);
export const dummyData_invalid = dummyData_allBulk.filter(
  (candidate) => candidate.status === "Invalid",
);
export const dummyData_duplicate = dummyData_allBulk.filter(
  (candidate) => candidate.status === "Duplicate",
);

const BulkInvite = () => {
  const [uploadResponse, setUploadResponse] = useState<any>(null);
  const tabs = [
    {
      id: "allApplicants",
      label: "Valid Applicants",
      data: uploadResponse?.success,
      count: uploadResponse?.success_count || 0,
    },
    {
      id: "invalidApplicants",
      label: "Invalid Applicants",
      data: uploadResponse?.failed,
      count: uploadResponse?.failed_count || 0,
    },
  ];

  const [activeTab, setActiveTab] = useState(tabs[0]?.id);
  const [uploadedExcelFile, setUploadedExcelFile] = useState<any>(null);
  const [allItemList, setAllItemList] = useState<any[]>(
    tabs.find((d) => d.id === activeTab)?.data || [],
  );

  const [errors, setErrors] = useState<ErrorType | null>(null);
  const [filterData, setFilterData] = useState<FilterTypes>(initialFilters);

  const { selectedItem: selectedPackage } = usePackageByDepartmentId(
    filterData?.department,
    filterData?.package,
  );

  // API
  const { callApi, loading: loadingUpload } = useApi<any>(
    webservice_url.uploadBulkInvite,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      dataIndex: "sr_no",
      onCell: () => cellWidth(100),
      render: (text: string, record: any, index: number) => index + 1,
      sorter: false,
    },
    {
      title: "First Name",
      dataIndex: "firstName",
      onCell: () => cellWidth(160),
      render: (text: any) => text || "-",
      sorter: (a, b) => safeStringSort(a.firstName, b.firstName),
    },
    {
      title: "Middle Name",
      dataIndex: "middleName",
      onCell: () => cellWidth(160),
      render: (text: any) => text || "-",
      sorter: (a, b) => safeStringSort(a.middleName, b.middleName),
    },
    {
      title: "Last Name",
      dataIndex: "lastName",
      onCell: () => cellWidth(160),
      render: (text: any) => text || "-",
      sorter: (a, b) => safeStringSort(a.lastName, b.lastName),
    },
    {
      title: "Applicant Mobile Number",
      dataIndex: "phone",
      onCell: () => cellWidth(160),
      render: (text: any) => text || "-",
      sorter: (a: any, b: any) => safeNumberSort(a.phone, b.phone),
    },
    {
      title: "Applicant Email Id",
      dataIndex: "email",
      onCell: () => cellWidth(160),
      render: (text: any) => text || "-",
      sorter: (a, b) => safeStringSort(a.email, b.email),
    },
    {
      title: "Applicant Registration Number/CRN",
      dataIndex: "applicantRegistrationNumberCrn",
      onCell: () => cellWidth(240),
      render: (text: any) => text || "-",
      sorter: (a, b) =>
        safeStringSort(
          a.applicantRegistrationNumberCrn,
          b.applicantRegistrationNumberCrn,
        ),
    },
    {
      title: "Applicant Employee Id/Code",
      dataIndex: "employeeId",
      onCell: () => cellWidth(200),
      render: (text: any) => text || "-",
      sorter: (a, b) => safeStringSort(a.employeeId, b.employeeId),
    },
    {
      title: "Joining Date",
      dataIndex: "joiningDate",
      onCell: () => cellWidth(160),
      render: (text: any) => {
        const date = text ? formatDateDDMMMMYYYY(text) : "-";
        return <div>{date}</div>;
      },
      sorter: (a: any, b: any) => safeDateSort(a.joiningDate, b.joiningDate),
    }, 
  ];

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(
        "/assets/files/bulk_invite_sample_5_candidates.xlsx",
      );
      const arrayBuffer = await response.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);

      const sheet = workbook.worksheets[0];
      const headerRow = sheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF26522" },
        };
        cell.font = {
          color: { argb: "FFFFFFFF" },
          bold: true,
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
      headerRow.commit();

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "bulk-sample-template.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Template download failed:", error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;
    setUploadedExcelFile(file);
 
    document
      .getElementById("excel-data")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });

    handleClearError(setErrors, "file");
  };
 
  const escapeHtml = (value: any) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const showFailedRowsPopup = (data: any) => {
    const failed: any[] = Array.isArray(data?.failed) ? data.failed : [];
    if (failed.length === 0) return;

    const successCount = data?.success_count || 0;
    const failedCount = data?.failed_count || failed.length;

    const rowsHtml = failed
      .map((row) => {
        const name = [row?.firstName, row?.middleName, row?.lastName]
          .filter(Boolean)
          .join(" ");

        return `
        <div style="padding:10px 12px;margin-bottom:8px;border:1px solid #f5c2c7;
                    border-left:4px solid #dc3545;border-radius:8px;background:#fff5f5;">
          <div style="font-weight:600;color:#1b2a3a;">
            Row ${escapeHtml(row?.row_number ?? "-")}${name ? ` · ${escapeHtml(name)}` : ""}
          </div>
          ${
            row?.field
              ? `<div style="font-size:12px;color:#64748b;margin-top:2px;">
                   Field: <b>${escapeHtml(row.field)}</b>
                 </div>`
              : ""
          }
          <div style="font-size:13px;color:#b02a37;margin-top:4px;">
            ${escapeHtml(row?.reason || "Invalid data")}
          </div>
        </div>`;
      })
      .join("");

    Swal.fire({
      icon: "warning",
      title: `${failedCount} candidate${failedCount > 1 ? "s" : ""} failed`,
      html: `
      <div style="font-size:14px;margin-bottom:12px;color:#475569;">
        ${successCount} uploaded successfully, ${failedCount} failed.
        Please correct the rows below and upload again.
      </div>
      <div style="max-height:320px;overflow-y:auto;text-align:left;">
        ${rowsHtml}
      </div>`,
      confirmButtonText: "OK",
      width: 600,
    });
  };

  const showValidationErrorPopup = (errData: any): boolean => {
    const rows: any[] = Array.isArray(errData?.details?.failed_rows)
      ? errData.details.failed_rows
      : [];
    if (rows.length === 0) return false;

    const totalRows = errData?.details?.total_rows ?? rows.length;

    const rowsHtml = rows
      .map((row) => {
        const name = [row?.firstName, row?.lastName]
          .map((v) => String(v ?? "").trim())
          .filter(Boolean)
          .join(" ");
 
        const reasons = String(row?.reason || "Invalid data")
          .split(";")
          .map((r) => r.trim())
          .filter(Boolean);

        return `
      <div style="padding:10px 12px;margin-bottom:8px;border:1px solid #f5c2c7;
                  border-left:4px solid #dc3545;border-radius:8px;background:#fff5f5;">
        <div style="font-weight:600;color:#1b2a3a;">
          Row ${escapeHtml(row?.row_number ?? "-")}${name ? ` · ${escapeHtml(name)}` : ""}
        </div>
        ${
          row?.email
            ? `<div style="font-size:12px;color:#64748b;margin-top:2px;">${escapeHtml(row.email)}</div>`
            : ""
        }
        <ul style="margin:6px 0 0;padding-left:18px;font-size:13px;color:#b02a37;">
          ${reasons.map((r) => `<li>${escapeHtml(r)}</li>`).join("")}
        </ul>
      </div>`;
      })
      .join("");

    Swal.fire({
      icon: "error",
      title: escapeHtml(errData?.error || "Excel validation failed"),
      html: `
      <div style="font-size:14px;margin-bottom:12px;color:#475569;">
        ${rows.length} of ${totalRows} rows have errors.
        Please correct them in the Excel file and upload again.
      </div>
      <div style="max-height:360px;overflow-y:auto;text-align:left;">
        ${rowsHtml}
      </div>`,
      confirmButtonText: "OK",
      width: 650,
    });

    return true;
  };

  const handleUploadBulkData = async () => {
    try {
      setUploadResponse(null);

      const payload = {
        file: uploadedExcelFile,
        project_id: filterData?.project ?? "",
        program_country_id: filterData?.location ?? "",
        program_id: filterData?.program ?? "",
        department_id: filterData?.department ?? "",
        package_id: filterData?.package ?? "",
      };

      const formData = new FormData();

      Object.entries(payload).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        if (value instanceof File || value instanceof Blob) {
          formData.append(key, value);
        } else {
          formData.append(key, String(value));
        }
      });

      const res = await callApi(formData);

      if (res?.success) {
        const data = res?.data || null;
        setUploadResponse(data);
        setActiveTab(tabs[0]?.id); 
        setAllItemList(Array.isArray(data?.success) ? data?.success : []); 
        if ((data?.success_count || 0) > 0) {
          toast.success(res?.message || "Uploaded successfully!");
        } 
        showFailedRowsPopup(data);
      }
    } catch (error: any) {
      console.error("Submission failed:", error);
      const errData = error?.response?.data; 
      const shown = showValidationErrorPopup(errData);
      if (!shown) {
        toast.error(errData?.error ?? "Invite failed");
      }
    }
  }; 
  // return (
  //   <div className="pb-4">
  //     <div> 
  //       <div className="mb-4">
  //         <div className="fw-semibold text-dark mb-2">Instructions:</div>

  //         <div className=" mb-1">1. Select Package and Place Order.</div>

  //         <div className=" mb-1">
  //           2. Download template
  //           <i
  //             className="ti ti-download text-primary fs-18 ms-1"
  //             style={{ cursor: "pointer" }}
  //             onClick={handleDownloadTemplate}
  //           ></i>
  //           .
  //         </div> 
  //         <div className="mt-2">
  //           <span className="fw-bold me-2">Note:-</span>
  //           {/* <br /> */}
  //           In case CRN(Column-G), employee id(Column-H) & joining
  //           date(Column-I) is not available please leave blank in excel.
  //         </div>
  //       </div> 
  //       <div className="py-2"></div> 
  //       <CommonFilterSelect
  //         filterData={filterData}
  //         colClass={"col-6 col-sm-4 col-md-3 col-xl-2"}
  //         handleGetFilters={(data) => setFilterData(data)}
  //       />
  //       {!filterData?.package && <div className="py-5 my-5"></div>}
  //     </div> 
  //     {!!filterData?.package && (
  //       <div className="mt-4 pb-4">
  //         {selectedPackage?.sop && (
  //           <>
  //             <div className=" mb-2 fw-semibold">Package Scope</div>
  //             <div className=" p-2 text-dark fw-semibold border rounded-3">
  //               {selectedPackage?.sop} 
  //             </div>
  //           </>
  //         )}
  //         <div className="mt-4 pt-3 fw-semibold text-dark mb-2">Step 3:</div>
  //         <div className="col-6 col-md-4 mb-3">
  //           <label htmlFor="Submit-Link" className="form-label formfield-label">
  //             Submit upload link to <span className="text-danger">*</span>
  //           </label>
  //           <select
  //             id="link"
  //             name="link" 
  //             className={`form-select select_field formfield-control ${
  //               errors?.gender ? "is-invalid" : ""
  //             }`.trim()} 
  //           >
  //             <option value="Candidate">Candidate</option> 
  //           </select>
  //           {errors?.gender && <ErrorMessage message={errors?.gender} />}
  //         </div>

  //         <div className="pt-3 fw-semibold text-dark mb-2">Step 4:</div>
  //         <div className="mb-3">
  //           <label htmlFor="file" className="form-label fw-semibold">
  //             Upload Excel File
  //           </label>

  //           <div className="d-flex align-items-center gap-2">
  //             <input
  //               type="file"
  //               name="file"
  //               className="form-control grow-1"
  //               accept=".xls,.xlsx,.pdf"
  //               style={{ maxWidth: 400 }}
  //               onChange={handleFileChange}
  //               disabled={loadingUpload}
  //             />
  //             <div>
  //               <button
  //                 type="button"
  //                 className="btn btn-primary py-2"
  //                 style={{ width: "max-content" }}
  //                 onClick={handleUploadBulkData}
  //                 disabled={loadingUpload}
  //               >
  //                 {loadingUpload ? "Uploading..." : "Upload Excel"}
  //               </button>
  //             </div>
  //           </div>
  //           <ErrorMessage message={errors?.file} />
  //         </div>

  //         <div className="mt-2">
  //           <span className="fw-bold pe-2 d-inline-block">Note:-</span>
  //           {/* <br /> */}
  //           You can Upload 50 candidates at one time.
  //         </div>

  //         {!uploadResponse && (
  //           <>
  //             {!loadingUpload ? (
  //               <div className="py-5 my-3"></div>
  //             ) : (
  //               <div>
  //                 <Loading />
  //               </div>
  //             )}
  //           </>
  //         )} 
  //         {!!uploadResponse && (
  //           <div id="excel-data">
  //             <div className="mt-3 fw-semibold text-dark mb-3">
  //               Step 5: Excel data will be populated below in case of duplicate
  //               and incorrect data candidate will be labelled as Invalid.
  //             </div> 
  //             <TabItems
  //               tabList={tabs}
  //               activeTab={activeTab}
  //               tabCountClass="d-flex align-items-center gap-1"
  //               handleClick={(d) => {
  //                 setActiveTab(d.id);
  //                 setAllItemList(d.data);
  //               }}
  //             />
 
  //             <div className="mt-3">
  //               <Table
  //                 dataSource={allItemList}
  //                 columns={columns}
  //                 Selection={false}
  //                 hideSizeChanger={true} 
  //               />
  //             </div> 
  //           </div>
  //         )}
  //       </div>
  //     )}
  //   </div>
  // );
return (
  <div className="pb-4">
    <div>
      {/* Instructions */}
      <div className="mb-4">
        <div className="fw-semibold text-dark mb-2">
          Instructions:
        </div>

        <div className="mb-1">
          1. Select the organization, account, location, program,
          department, and package.
        </div>

        <div className="mb-1">
          2. Download the Excel template and fill in the applicant details.
          <button
            type="button"
            className="btn btn-link text-primary p-0 ms-2 text-decoration-none"
            onClick={handleDownloadTemplate}
            title="Download Excel template"
          >
            Download template
            <i className="ti ti-download fs-18 ms-1" />
          </button>
        </div>

        <div className="mt-2">
          <span className="fw-bold me-2">Note:</span>
          If CRN (Column G), Employee ID (Column H), or Joining Date
          (Column I) is unavailable, leave the corresponding cells blank
          in the Excel template.
        </div>
      </div>

      {/* Package Filters */}
      <div className="py-2">
        <CommonFilterSelect
          filterData={filterData}
          colClass="col-12 col-sm-6 col-md-4 col-xl-2"
          handleGetFilters={(data) => setFilterData(data)}
        />
      </div>

      {!filterData?.package && <div className="py-3" />}
    </div>

    {!!filterData?.package && (
      <div className="mt-4 pb-4">
        {/* Package Scope */}
        {selectedPackage?.sop && (
          <>
            <div className="mb-2 fw-semibold text-dark">
              Package Scope
            </div>

            <div className="p-3 text-dark border rounded-3">
              {selectedPackage.sop}
            </div>
          </>
        )}

        {/* Step 3 */}
        <div className="mt-4 fw-semibold text-dark mb-2">
          Step 3: Submit Upload Link
        </div>

        <div className="col-12 col-md-6 col-xl-4 mb-3">
          <label
            htmlFor="link"
            className="form-label formfield-label"
          >
            Submit upload link to <span className="text-danger">*</span>
          </label>

          <select
            id="link"
            name="link"
            className="form-select select_field formfield-control"
          >
            <option value="Candidate">Candidate</option>
          </select>

          {errors?.gender && (
            <ErrorMessage message={errors.gender} />
          )}
        </div>

        {/* Step 4 */}
        <div className="mt-4 fw-semibold text-dark mb-2">
          Step 4: Upload Applicant Excel File
        </div>

        <div className="mb-3">
          <label htmlFor="file" className="form-label">
            Select Excel file <span className="text-danger">*</span>
          </label>

          <div className="d-flex flex-wrap align-items-center gap-2">
            <input
              id="file"
              type="file"
              name="file"
              className="form-control"
              accept=".xls,.xlsx"
              style={{ maxWidth: 400 }}
              onChange={handleFileChange}
              disabled={loadingUpload}
            />

            <button
              type="button"
              className="btn btn-primary py-2"
              onClick={handleUploadBulkData}
              disabled={loadingUpload}
            >
              {loadingUpload ? "Uploading..." : "Upload Excel"}
            </button>
          </div>

          <ErrorMessage message={errors?.file} />
        </div>

        <div className="mt-2 mb-3">
          <span className="fw-bold me-2">Note:</span>
          You can upload a maximum of 50 candidates at one time.
        </div>

        {/* Upload Loading / Results */}
        {!uploadResponse && loadingUpload && (
          <div className="py-3">
            <Loading />
          </div>
        )}

        {!!uploadResponse && (
          <div id="excel-data" className="mt-4">
            <div className="fw-semibold text-dark mb-3">
              Step 5: Review Uploaded Candidates
            </div>

            <p className="text-muted mb-3">
              Review the uploaded data below. Duplicate or incorrect
              candidate records will be marked as invalid.
            </p>

            <TabItems
              tabList={tabs}
              activeTab={activeTab}
              tabCountClass="d-flex align-items-center gap-1"
              handleClick={(d) => {
                setActiveTab(d.id);
                setAllItemList(d.data);
              }}
            />

            <div className="mt-3">
              <Table
                dataSource={allItemList}
                columns={columns}
                Selection={false}
                hideSizeChanger={true}
              />
            </div>
          </div>
        )}
      </div>
    )}
  </div>
);
};

export default BulkInvite;
